import { Timestamp } from "firebase/firestore";
import { FirebaseData } from "../../General/FirebaseData";
import { PropertyData } from "../PropertyData";
import BookingUnitData from "./BookingUnitData";
import { t } from "i18next";

import CollectionLoader from "../../General/CollectionLoader";
import BookingKeyData from "./BookingKeyData";
import { LocalData } from "../../General/LocalData";
import { Role } from "../../User/Role";
import { AuthenticationTokenData } from "../../General/AuthenticationTokenData";
import { UnitData } from "../Units/UnitData";
import BookingInformationData from "./BookingInformationData";

type BookingState = "verifying" | "verified" | "cancelled";

export default class BookingData extends FirebaseData {
  bookingUnit?: BookingUnitData;
  bookingOwnerUnit?: UnitData;
  startDate?: Date;
  endDate?: Date;
  state: BookingState = "verifying";
  cancellationReason: string = "";
  bookingNumber: string = "";
  digitalKeys: CollectionLoader<BookingKeyData>;
  information: CollectionLoader<BookingInformationData>;
  pendingDateChange: boolean = false;
  lastVerificationSendDate?: Date;

  constructor(id: string, collection: string, parent?: FirebaseData) {
    super(id, collection, parent);
    this.digitalKeys = new CollectionLoader<BookingKeyData>("keys", this, BookingKeyData);
    this.information = new CollectionLoader<BookingInformationData>("information", this, BookingInformationData);
  }

  setup(data: { [key: string]: any }) {
    if (typeof data.bookingUnitID === "string" && this.parent instanceof PropertyData) {
      this.bookingUnit = this.parent.bookingUnits.addData(data.bookingUnitID);
    }
    if (data.startDate instanceof Timestamp) {
      this.startDate = data.startDate.toDate();
    } else if (data.startDate instanceof Date) {
      this.startDate = data.startDate;
    }
    if (data.endDate instanceof Timestamp) {
      this.endDate = data.endDate.toDate();
    } else if (data.endDate instanceof Date) {
      this.endDate = data.endDate;
    }
    if (data.lastVerificationSendDate instanceof Timestamp) {
      this.lastVerificationSendDate = data.lastVerificationSendDate.toDate();
    } else if (data.lastVerificationSendDate instanceof Date) {
      this.lastVerificationSendDate = data.lastVerificationSendDate;
    }
    if (typeof data.state === "string") {
      this.state = data.state as BookingState;
    }
    if (typeof data.cancellationReason === "string") {
      this.cancellationReason = data.cancellationReason;
    }
    if (typeof data.bookingNumber === "string") {
      this.bookingNumber = data.bookingNumber;
    }
    if (typeof data.bookingOwnerUnitID === "string") {
      this.bookingOwnerUnit = this.parent ? (this.parent as PropertyData).getUnitWithId(data.bookingOwnerUnitID) : undefined;
    }
    this.pendingDateChange = Object.keys(data.proposedDateChange ?? {}).length > 0;

    super.setup(data);
  }

  canView(): boolean {
    const member = (this.parent as PropertyData).members.getData(LocalData.currentUser.id);
    return this.bookingUnit?.bookingPrivacy === "public" || member?.roles.includes(Role.LANDLORD) || this.isOwnerOfBooking();
  }

  canAdministrate(): boolean {
    if (this.isOwnerOfBooking()) {
      return true;
    }
    if (this.parent instanceof PropertyData) {
      const member = (this.parent as PropertyData).members.getData(LocalData.currentUser.id);
      return (member?.roles.includes(Role.LANDLORD) && member.isAdmin) ?? false;
    }
    return false;
  }

  isOwnerOfBooking(): boolean {
    if (this.bookingUnit?.bookingGrouping === "individually") {
      return this.createdBy?.user === LocalData.currentUser;
    }
    if (this.parent instanceof PropertyData) {
      const member = this.parent.members.getData(LocalData.currentUser.id);
      if (member?.units.find((unit) => unit.id === this.bookingOwnerUnit?.id)) {
        return true;
      }
    }
    const externalProperty = AuthenticationTokenData.token?.claims.properties?.find((p) => p.data === this.parent);
    if (
      externalProperty &&
      ((Array.isArray(externalProperty.bookings) && externalProperty.bookings.includes(this)) ||
        (Array.isArray(externalProperty.units) && this.bookingOwnerUnit && externalProperty.units.includes(this.bookingOwnerUnit)))
    ) {
      return true;
    }
    return false;
  }

  getTitle() {
    if (!this.canView()) {
      return t("main.hidden");
    }

    let title = "";
    const data = this.information.getData("general");
    if (data && data.title) {
      title = data.title;
    }

    return title;
  }
}
